import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { apiFetch, ApiError } from '../lib/api';

/**
 * Auth provider. The browser holds no Supabase keys or tokens — the session
 * lives in an HttpOnly cookie set by the Worker. This provider's only job is
 * to keep React state in sync with what /api/auth/me reports.
 */

export interface User {
  id: string;
  email?: string;
  app_metadata?: Record<string, unknown>;
  user_metadata?: Record<string, unknown>;
}

interface AuthState {
  user: User | null;
  loading: boolean;
  signIn: (email: string, password: string) => Promise<void>;
  signOut: () => Promise<void>;
  sendPasswordReset: (email: string) => Promise<void>;
  requestOtp: (email: string) => Promise<void>;
  verifyOtp: (email: string, token: string) => Promise<void>;
  updatePassword: (newPassword: string) => Promise<void>;
  refresh: () => Promise<void>;
}

const AuthContext = createContext<AuthState | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  async function loadUser() {
    try {
      const { user } = await apiFetch<{ user: User }>('/api/auth/me');
      setUser(user);
    } catch (err) {
      if (!(err instanceof ApiError) || err.status !== 401) {
        // Network or unexpected error — surface so the user knows something's off.
        console.error('Failed to load user', err);
      }
      setUser(null);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    loadUser();
  }, []);

  const value = useMemo<AuthState>(
    () => ({
      user,
      loading,
      async signIn(email, password) {
        const { user } = await apiFetch<{ user: User }>('/api/auth/login', {
          method: 'POST',
          body: JSON.stringify({ email, password }),
        });
        setUser(user);
      },
      async signOut() {
        await apiFetch('/api/auth/logout', { method: 'POST' });
        setUser(null);
      },
      async sendPasswordReset(email) {
        await apiFetch('/api/auth/forgot-password', {
          method: 'POST',
          body: JSON.stringify({ email }),
        });
      },
      async requestOtp(email) {
        await apiFetch('/api/auth/otp/request', {
          method: 'POST',
          body: JSON.stringify({ email }),
        });
      },
      async verifyOtp(email, token) {
        const { user } = await apiFetch<{ user: User }>('/api/auth/otp/verify', {
          method: 'POST',
          body: JSON.stringify({ email, token }),
        });
        setUser(user);
      },
      async updatePassword(newPassword) {
        await apiFetch('/api/auth/update-password', {
          method: 'POST',
          body: JSON.stringify({ password: newPassword }),
        });
      },
      refresh: loadUser,
    }),
    [user, loading],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

// eslint-disable-next-line react-refresh/only-export-components
export function useAuth(): AuthState {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within an AuthProvider');
  return ctx;
}
